import { Directive, ElementRef, AfterViewInit, OnDestroy, OnChanges, Input } from '@angular/core';

// Animates a table row in once, the first time it scrolls into view —
// e.g. dashboard-v2's admissions table and fees-v2's class breakdown
// table. Starts with "dv2-row-pending" (opacity 0, translateY — each
// consuming component's own CSS defines this class, see
// dashboard-v2.component.css / feesmanage-v2.component.css), and swaps to
// "dv2-row-in" with a per-row stagger delay once observed, then stops
// observing (animates once per row) and clears the inline delay after the
// transition ends so hover stays instant afterwards.
const MAX_STAGGER_STEPS = 8;
const STAGGER_STEP_MS = 45;

@Directive({
  selector: '[rowEntrance]',
})
export class RowEntranceDirective implements AfterViewInit, OnChanges, OnDestroy {
  // Changing this (e.g. a new page/filter of rows) re-arms the row to
  // animate again, since it's conceptually a new row appearing — same
  // reasoning as trackBy on the *ngFor driving these.
  @Input() rowEntrance: unknown;

  private observer?: IntersectionObserver;
  private hasAnimated = false;

  constructor(private elementRef: ElementRef<HTMLElement>) {}

  ngAfterViewInit(): void {
    this.arm();
  }

  ngOnChanges(): void {
    if (this.hasAnimated) {
      this.hasAnimated = false;
      this.arm();
    }
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }

  private arm(): void {
    const el = this.elementRef.nativeElement;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      el.classList.remove('dv2-row-pending');
      return;
    }
    el.classList.add('dv2-row-pending');
    this.observer?.disconnect();
    this.observer = new IntersectionObserver((entries) => {
      const visibleRows = entries
        .filter((entry) => entry.isIntersecting)
        .map((entry) => entry.target as HTMLElement)
        .sort((a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top);

      visibleRows.forEach((row, index) => {
        const delayMs = Math.min(index, MAX_STAGGER_STEPS) * STAGGER_STEP_MS;
        row.style.transitionDelay = `${delayMs}ms`;
        row.classList.remove('dv2-row-pending');
        row.classList.add('dv2-row-in');
        const clearDelay = () => {
          row.style.transitionDelay = '';
          row.removeEventListener('transitionend', clearDelay);
        };
        row.addEventListener('transitionend', clearDelay);
        this.observer?.unobserve(row);
      });
    }, { rootMargin: '0px 0px -40px 0px' });
    this.observer.observe(el);
    this.hasAnimated = true;
  }
}
